import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ClipboardCheck, ExternalLink, FlaskConical, KeyRound, ShieldAlert, Users } from 'lucide-react';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

function buildQuery(params) {
  const search = new URLSearchParams();
  if (params.identityId) search.set('identity_id', params.identityId);
  if (params.accountId) search.set('account_id', params.accountId);
  if (params.requestId) search.set('request_id', params.requestId);
  const query = search.toString();
  return query ? `?${query}` : '';
}

function StepCard({ step, detail, to, label, accent = 'zinc', links = [] }) {
  const accentStyles = {
    zinc: 'border-zinc-200 bg-white text-zinc-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    sky: 'border-sky-200 bg-sky-50 text-sky-900',
  };

  return (
    <article className={`border p-4 ${accentStyles[accent] || accentStyles.zinc}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest opacity-70">{step}</p>
          <p className="mt-2 text-sm leading-6">{detail}</p>
        </div>
        <span className="inline-flex border border-zinc-200 bg-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Guide</span>
      </div>
      <div className="mt-4">
        <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
          {label}
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
        </Link>
        {links.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {links.map((link) => (
              <Link key={link.to} to={link.to} className="border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-700 hover:border-zinc-400">
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

function StatusCard({ title, detail, to, linkLabel, icon: Icon }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center border border-zinc-200 bg-zinc-50 text-zinc-700">
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </span>
        <div className="min-w-0">
          <p className="font-medium text-zinc-900">{title}</p>
          <p className="mt-1 text-sm leading-6 text-zinc-600">{detail}</p>
          <div className="mt-3">
            <Link to={to} className="text-sm text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
              {linkLabel}
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function DigitalKeyDemoRunner() {
  const [searchParams] = useSearchParams();
  const identityId = searchParams.get('identity_id') || '';
  const accountId = searchParams.get('account_id') || '';
  const requestId = searchParams.get('request_id') || '';

  const query = useMemo(() => buildQuery({ identityId, accountId, requestId }), [accountId, identityId, requestId]);
  const hasContext = Boolean(identityId || accountId || requestId);

  const steps = [
    {
      step: 'Step 1',
      detail: 'Create or select an idem identity from an approved KYC/IDV-derived internal record.',
      to: '/internal/identities',
      label: 'Open identities',
      accent: 'zinc',
    },
    {
      step: 'Step 2',
      detail: 'Create or select a digital key account linked to the identity and internal lab flow.',
      to: '/internal/digital-key-accounts',
      label: 'Open digital key accounts',
      accent: 'amber',
    },
    {
      step: 'Step 3',
      detail: 'Use the Start lab login action on a digital key account row to seed the OIDC Lab.',
      to: `/internal/digital-key-accounts${query}`,
      label: 'Continue to account overview',
      accent: 'emerald',
    },
    {
      step: 'Step 4',
      detail: 'Create the simulated relying-party login request in OIDC Lab and keep it internal only.',
      to: `/internal/oidc-lab${query}`,
      label: 'Open OIDC Lab',
      accent: 'sky',
    },
    {
      step: 'Step 5',
      detail: 'Review the linked login request in Authenticator Preview and approve or deny it internally.',
      to: `/internal/authenticator-preview${requestId ? `?request_id=${encodeURIComponent(requestId)}` : ''}`,
      label: 'Open Authenticator Preview',
      accent: 'zinc',
    },
    {
      step: 'Step 6',
      detail: 'Return to OIDC Lab, finalize the returned request, and inspect safe claims only.',
      to: `/internal/oidc-lab${query}`,
      label: 'Return to OIDC Lab',
      accent: 'emerald',
    },
    {
      step: 'Step 7',
      detail: 'Review readiness and evidence notes before any broader protocol or recognition discussion.',
      to: '/internal/fas-readiness',
      label: 'Open readiness matrix',
      accent: 'amber',
      links: [
        { to: '/internal/fas-readiness', label: 'FAS readiness' },
        { to: '/internal/audit-governance-pack', label: 'Audit governance' },
        { to: '/internal/bosa-evidence-pack', label: 'BOSA evidence pack' },
      ],
    },
  ];

  const cards = [
    {
      title: 'Identity operations',
      detail: 'Use the identity operations page to work from an approved verification-derived source.',
      to: '/internal/identities',
      linkLabel: 'Open identity operations',
      icon: Users,
    },
    {
      title: 'Digital key account',
      detail: 'Check the account link, lifecycle state, and safe login-request row actions.',
      to: '/internal/digital-key-accounts',
      linkLabel: 'Open digital key accounts',
      icon: KeyRound,
    },
    {
      title: 'OIDC Lab',
      detail: 'Simulate the login request and safe claim-release path for the internal lab client.',
      to: `/internal/oidc-lab${query}`,
      linkLabel: 'Open OIDC Lab',
      icon: FlaskConical,
    },
    {
      title: 'Authenticator Preview',
      detail: 'Approve or deny the request and return it to the lab for finalization.',
      to: `/internal/authenticator-preview${requestId ? `?request_id=${encodeURIComponent(requestId)}` : ''}`,
      linkLabel: 'Open authenticator preview',
      icon: ShieldAlert,
    },
    {
      title: 'Safe claims preview',
      detail: 'Confirm the final internal output shows safe claims only and no sensitive evidence.',
      to: `/internal/oidc-lab${query}`,
      linkLabel: 'Review safe claims',
      icon: ClipboardCheck,
    },
    {
      title: 'Audit / readiness evidence',
      detail: 'Review the readiness and evidence packs that support the internal demo flow.',
      to: '/internal/audit-governance-pack',
      linkLabel: 'Open audit governance',
      icon: ExternalLink,
    },
  ];

  return (
    <div data-testid="digital-key-demo-runner-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Digital Key Demo Runner" showDashboard={false} />

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Digital Key Demo Runner</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">Internal lab runbook for idem identity → digital key account → login approval → safe claims.</p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-medium">Internal lab guide only</p>
            <p className="mt-1">This page is an internal lab guide. It is not a public login product, production OIDC provider, official FAS/CSAM integration, certification, LoA, or compliance statement.</p>
          </div>
        </div>
      </section>

      {hasContext && (
        <section className="mt-6 border border-sky-200 bg-sky-50 p-5 text-sm text-sky-900">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-medium">Current lab context</p>
              <p className="mt-1 text-sky-800">Safe query parameters are preserved so you can move between the internal pages without copying them again.</p>
            </div>
            <span className="inline-flex border border-sky-200 bg-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-sky-800">Internal only</span>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="border border-sky-200 bg-white p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-500">Identity ID</p>
              <p className="mt-1 break-all font-mono text-xs text-sky-900">{identityId || '—'}</p>
            </div>
            <div className="border border-sky-200 bg-white p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-500">Account ID</p>
              <p className="mt-1 break-all font-mono text-xs text-sky-900">{accountId || '—'}</p>
            </div>
            <div className="border border-sky-200 bg-white p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-500">Request ID</p>
              <p className="mt-1 break-all font-mono text-xs text-sky-900">{requestId || '—'}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/internal/oidc-lab${query}`} className="border border-sky-200 bg-white px-3 py-2 text-sky-800 hover:border-sky-300">
              Open OIDC Lab with context
            </Link>
            <Link to={`/internal/authenticator-preview${requestId ? `?request_id=${encodeURIComponent(requestId)}` : ''}`} className="border border-sky-200 bg-white px-3 py-2 text-sky-800 hover:border-sky-300">
              Open authenticator preview
            </Link>
          </div>
        </section>
      )}

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">Guided flow</h2>
            <p className="mt-1 max-w-4xl text-sm text-zinc-600">Follow the internal steps in order. Each link stays inside the platform-admin-only internal area and avoids sensitive data exposure.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Identity</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Lab login</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Authenticator review</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Safe claims</span>
          </div>
        </div>
        <div className="mt-4 grid gap-3 xl:grid-cols-2">
          {steps.map((item) => <StepCard key={item.step} {...item} />)}
        </div>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => <StatusCard key={card.title} {...card} />)}
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Short guidance</h2>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          This runner is a navigation aid only. Use it to move through the existing internal identities, accounts, OIDC lab, authenticator preview, and readiness pages without exposing public login, production tokens, or regulated identifier data.
        </p>
      </section>
    </div>
  );
}
